import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';
import type { SkillTreeIntroWindow } from './skill-tree-intro';
import type { SkillTreeIntroClock, SkillTreeIntroMotion, SkillTreeIntroPartOptions } from './skill-tree-intro-clock';

/** The intro's clock, while the tree is mid-intro (or held shrunk back after closing); null when it is settled. */
export const SkillTreeIntroClockContext = createContext<SkillTreeIntroClock | null>(null);

/**
 * Registers an element with the intro's clock while the intro runs, so the
 * clock poses it each frame; returns a ref to attach. With no clock or no
 * window the element is left as is.
 */
export function useSkillTreeIntroPart(
    window: SkillTreeIntroWindow | null | undefined,
    motion: SkillTreeIntroMotion,
    options: SkillTreeIntroPartOptions = {},
): (element: SVGElement | null) => void {
    const clock = useContext(SkillTreeIntroClockContext);
    const elementRef = useRef<SVGElement | null>(null);
    const { origin, from } = options;
    // Primitive dependencies, so a re-render with equal values doesn't re-register.
    const start = window?.start;
    const duration = window?.duration;
    const originX = origin?.x;
    const originY = origin?.y;
    const fromX = from?.x;
    const fromY = from?.y;
    return useCallback((element: SVGElement | null): void => {
        if (elementRef.current) clock?.unregister(elementRef.current);
        elementRef.current = element;
        if (!element || !clock || start === undefined || duration === undefined) return;
        clock.register(element, { start, duration }, motion, {
            origin: originX === undefined || originY === undefined ? undefined : { x: originX, y: originY },
            from: fromX === undefined || fromY === undefined ? undefined : { x: fromX, y: fromY },
        });
    }, [clock, start, duration, motion, originX, originY, fromX, fromY]);
}

interface SkillTreeIntroPartProps extends SkillTreeIntroPartOptions {
    readonly window: SkillTreeIntroWindow | null | undefined;
    readonly motion: SkillTreeIntroMotion;
    readonly className?: string;
    readonly children: ReactNode;
}

/** A group the intro's clock animates as one part (an entrance, a fade, the sigil's pulse). */
export function SkillTreeIntroPart({ window, motion, origin, from, className, children }: SkillTreeIntroPartProps): JSX.Element {
    const ref = useSkillTreeIntroPart(window, motion, { origin, from });
    return <g ref={ref} className={className}>{children}</g>;
}
